// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import ButtonModal from '@react/components/ButtonModal'
import { useOtChainState } from '@react/redux/store'
import { REPORT_DEFI_FEE_ACCRUAL_HISTORY } from '@react/reporting/ReportDescriptors'
import { ReportExplorer } from '@react/reporting/ReportExplorer'
import { ReportRowGeneratorClass } from '@react/reporting/ReportRowGeneratorClass'
import { TimeStep } from '@shared/Enums'
import { EthAddr } from '@shared/SchemaTypes'
import dayjs from 'dayjs'
import { JsonRpcProvider, ZeroAddress } from 'ethers'
import React, { useEffect, useState } from 'react'
import DefiFeeForm from './DefiFeeForm'

export default function DefiFeePage(props: { poolAddr: EthAddr }): React.ReactElement {
  const theme = useTheme()
  const [reportRowGeneratorClass, setReportRowGeneratorClass] = useState<ReportRowGeneratorClass | undefined>(undefined)
  const {
    chainConfiguration: { providerReadOnly, chainTimestampDelta },
    stageConfiguration: { serverApiBaseUrl, restAPIKey },
  } = useOtChainState()
  const hasRun = React.useRef(false)
  useEffect(() => {
    if (hasRun.current) return
    hasRun.current = true
    const _reportRowGeneratorClass = new ReportRowGeneratorClass({
      reportDescriptor: REPORT_DEFI_FEE_ACCRUAL_HISTORY,
      providerReadOnly: providerReadOnly as JsonRpcProvider,
      serverApiBaseUrl: serverApiBaseUrl,
      restAPIKey: restAPIKey,
      poolAddr: props.poolAddr,
      lenderAddr: ZeroAddress,
      endDateTime: dayjs().utc().add(chainTimestampDelta, 'seconds'),
      timeStep: TimeStep.Day,
    })
    setReportRowGeneratorClass(_reportRowGeneratorClass)

    _reportRowGeneratorClass.refreshData()
  }, [])

  return (
    <Paper
      data-testid="DefiFeePage"
      sx={{
        marginTop: '20px',
        marginBottom: 4,
        marginRight: '2rem',
        marginLeft: '2rem',
        padding: '24px',
        borderRadius: 4,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
        <Typography
          align="left"
          sx={{
            fontWeight: 600,
            fontSize: '2rem',
            color: '#171717',
          }}
          variant={'h4'}
        >
          Defi Fees
        </Typography>
        <ButtonModal disableClose={true} modalWidth={800} title="Fee Repayment Form" variant="primary">
          <DefiFeeForm poolAddr={props.poolAddr} />
        </ButtonModal>
      </Box>
      {reportRowGeneratorClass && <ReportExplorer reportRowGeneratorClass={reportRowGeneratorClass} />}
    </Paper>
  )
}
